import { useState } from "react";
import { FaTrash, FaSave, FaArrowUp, FaArrowDown, FaGripVertical } from "react-icons/fa";
import Button from "./Button";

interface LayoutField {
  fieldId: string;
  width?: string;
  fontSize?: string;
  bold?: boolean;
  italic?: boolean;
  showLabel?: boolean;
  showIcon?: boolean;
}

interface LayoutRow {
  fields: LayoutField[];
}

interface CardLayout {
  [zone: string]: any;
}

interface CardLayoutEditorProps {
  cardLayout: any;
  onChange: (layout: any) => void;
  fieldDefinitions: any[];
  onSave: () => void;
}

const PSEUDO_FIELDS = [
  { id: "__title", label: "Title", type: "pseudo" },
  { id: "__assignees", label: "Assignees", type: "pseudo" },
  { id: "__author", label: "Author", type: "pseudo" },
];

const WIDTH_OPTIONS = ["full", "1/2", "1/3", "2/3", "1/4", "3/4", "auto"];
const FONT_SIZE_OPTIONS = ["xs", "sm", "md", "lg"];

const ZONE_LABELS: Record<string, string> = {
  header: "Header",
  body: "Body",
  footer: "Footer",
};

function defaultLayout(): CardLayout {
  return {
    header: { rows: [{ fields: [{ fieldId: "__title", width: "full", fontSize: "md", bold: true, showLabel: false }] }] },
    body: { rows: [] },
    footer: { rows: [{ fields: [{ fieldId: "__assignees", width: "full", fontSize: "xs", showLabel: false }] }] },
  };
}

function ensureLayout(layout: any): CardLayout {
  if (!layout) return defaultLayout();
  return {
    header: layout.header || { rows: [] },
    body: layout.body || { rows: [] },
    footer: layout.footer || { rows: [] },
  };
}

export default function CardLayoutEditor({ cardLayout, onChange, fieldDefinitions, onSave }: CardLayoutEditorProps) {
  const layout = ensureLayout(cardLayout);
  const [editingField, setEditingField] = useState<{ zone: string; rowIdx: number; fieldIdx: number } | null>(null);
  const [draggedField, setDraggedField] = useState<{ zone: string; rowIdx: number; fieldIdx: number } | null>(null);
  const [dragOverTarget, setDragOverTarget] = useState<{ zone: string; rowIdx: number; fieldIdx: number } | null>(null);

  const allFields = [
    ...PSEUDO_FIELDS,
    ...fieldDefinitions.map((fd: any) => ({ id: String(fd.id), label: fd.label, type: fd.type })),
  ];

  const fieldLabel = (fieldId: string) => {
    const f = allFields.find((af) => af.id === fieldId);
    return f?.label || fieldId;
  };

  function updateLayout(newLayout: CardLayout) {
    onChange(newLayout);
  }

  function addRow(zone: string) {
    const newLayout = { ...layout };
    newLayout[zone].rows = [...newLayout[zone].rows, { fields: [] }];
    updateLayout(newLayout);
  }

  function removeRow(zone: string, rowIdx: number) {
    const newLayout = { ...layout };
    newLayout[zone].rows = newLayout[zone].rows.filter((_: any, i: number) => i !== rowIdx);
    updateLayout(newLayout);
  }

  function moveRow(zone: string, rowIdx: number, dir: "up" | "down") {
    const newLayout = { ...layout };
    const rows = [...newLayout[zone].rows];
    const target = dir === "up" ? rowIdx - 1 : rowIdx + 1;
    if (target < 0 || target >= rows.length) return;
    [rows[rowIdx], rows[target]] = [rows[target], rows[rowIdx]];
    newLayout[zone].rows = rows;
    updateLayout(newLayout);
  }

  function addFieldToRow(zone: string, rowIdx: number, fieldId: string) {
    const newLayout = { ...layout };
    newLayout[zone].rows = [...newLayout[zone].rows];
    newLayout[zone].rows[rowIdx] = {
      ...newLayout[zone].rows[rowIdx],
      fields: [...newLayout[zone].rows[rowIdx].fields, { fieldId, width: "full", fontSize: "sm", showLabel: true, showIcon: true }],
    };
    updateLayout(newLayout);
  }

  function removeField(zone: string, rowIdx: number, fieldIdx: number) {
    const newLayout = { ...layout };
    const row = { ...newLayout[zone].rows[rowIdx] };
    row.fields = row.fields.filter((_: any, i: number) => i !== fieldIdx);
    newLayout[zone].rows = [...newLayout[zone].rows];
    newLayout[zone].rows[rowIdx] = row;
    updateLayout(newLayout);
    if (editingField?.rowIdx === rowIdx && editingField?.fieldIdx === fieldIdx) {
      setEditingField(null);
    }
  }

  function moveFieldTo(
    fromZone: string, fromRowIdx: number, fromFieldIdx: number,
    toZone: string, toRowIdx: number, toFieldIdx: number
  ) {
    const newLayout = { ...layout };
    // Deep clone the rows for both zones
    newLayout[fromZone] = { rows: newLayout[fromZone].rows.map((r: any) => ({ ...r, fields: [...r.fields] })) };
    if (toZone !== fromZone) {
      newLayout[toZone] = { rows: newLayout[toZone].rows.map((r: any) => ({ ...r, fields: [...r.fields] })) };
    }

    const fromFields = newLayout[fromZone].rows[fromRowIdx].fields;
    const [moved] = fromFields.splice(fromFieldIdx, 1);

    // Adjust target index if removing shifted it in the same row
    let adjustedToIdx = toFieldIdx;
    if (fromZone === toZone && fromRowIdx === toRowIdx && fromFieldIdx < toFieldIdx) {
      adjustedToIdx = toFieldIdx - 1;
    }

    const toFields = newLayout[toZone].rows[toRowIdx].fields;
    toFields.splice(adjustedToIdx, 0, moved);

    updateLayout(newLayout);
    setEditingField(null);
  }

  function updateFieldConfig(zone: string, rowIdx: number, fieldIdx: number, patch: Partial<LayoutField>) {
    const newLayout = { ...layout };
    const fields = [...newLayout[zone].rows[rowIdx].fields];
    fields[fieldIdx] = { ...fields[fieldIdx], ...patch };
    newLayout[zone].rows = [...newLayout[zone].rows];
    newLayout[zone].rows[rowIdx] = { ...newLayout[zone].rows[rowIdx], fields };
    updateLayout(newLayout);
  }

  function renderZone(zone: string) {
    const zoneData = layout[zone];
    if (!zoneData) return null;

    return (
      <div key={zone} className="border rounded-lg p-3 bg-gray-50">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-semibold text-gray-700">{ZONE_LABELS[zone]}</h4>
          <button
            onClick={() => addRow(zone)}
            className="text-xs text-primary hover:text-primary-dark font-medium"
          >
            + Add row
          </button>
        </div>

        {zoneData.rows.length === 0 && (
          <p className="text-xs text-gray-400 italic">No rows. Add a row to place fields here.</p>
        )}

        <div className="space-y-2">
          {zoneData.rows.map((row: LayoutRow, rowIdx: number) => (
            <div key={rowIdx} className="bg-white rounded border p-2">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => moveRow(zone, rowIdx, "up")}
                    disabled={rowIdx === 0}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <FaArrowUp className="text-xs" />
                  </button>
                  <button
                    onClick={() => moveRow(zone, rowIdx, "down")}
                    disabled={rowIdx === zoneData.rows.length - 1}
                    className="text-gray-400 hover:text-gray-600 disabled:opacity-30"
                  >
                    <FaArrowDown className="text-xs" />
                  </button>
                  <span className="text-xs text-gray-400">Row {rowIdx + 1}</span>
                </div>
                <button
                  onClick={() => removeRow(zone, rowIdx)}
                  className="text-gray-400 hover:text-red-500"
                >
                  <FaTrash className="text-xs" />
                </button>
              </div>

              <div
                className="flex flex-wrap gap-1 items-center min-h-[28px]"
                onDragOver={(e) => {
                  if (draggedField) {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }
                }}
                onDrop={(e) => {
                  if (!draggedField) return;
                  e.preventDefault();
                  e.stopPropagation();
                  // Drop at end of this row
                  moveFieldTo(
                    draggedField.zone, draggedField.rowIdx, draggedField.fieldIdx,
                    zone, rowIdx, row.fields.length
                  );
                  setDraggedField(null);
                  setDragOverTarget(null);
                }}
              >
                {row.fields.map((field: LayoutField, fieldIdx: number) => {
                  const isEditing =
                    editingField?.zone === zone &&
                    editingField?.rowIdx === rowIdx &&
                    editingField?.fieldIdx === fieldIdx;
                  const isDragOver =
                    dragOverTarget?.zone === zone &&
                    dragOverTarget?.rowIdx === rowIdx &&
                    dragOverTarget?.fieldIdx === fieldIdx;

                  return (
                    <div
                      key={fieldIdx}
                      draggable
                      onDragStart={(e) => {
                        setDraggedField({ zone, rowIdx, fieldIdx });
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => {
                        setDraggedField(null);
                        setDragOverTarget(null);
                      }}
                      onDragOver={(e) => {
                        if (draggedField) {
                          e.preventDefault();
                          e.stopPropagation();
                          e.dataTransfer.dropEffect = "move";
                          setDragOverTarget({ zone, rowIdx, fieldIdx });
                        }
                      }}
                      onDrop={(e) => {
                        if (!draggedField) return;
                        e.preventDefault();
                        e.stopPropagation();
                        moveFieldTo(
                          draggedField.zone, draggedField.rowIdx, draggedField.fieldIdx,
                          zone, rowIdx, fieldIdx
                        );
                        setDraggedField(null);
                        setDragOverTarget(null);
                      }}
                      className={`relative bg-gray-100 rounded px-2 py-1 text-xs border ${
                        isEditing ? "border-primary" : isDragOver ? "border-primary border-dashed" : "border-transparent"
                      } ${draggedField && draggedField.zone === zone && draggedField.rowIdx === rowIdx && draggedField.fieldIdx === fieldIdx ? "opacity-50" : ""}`}
                      style={{ cursor: "grab" }}
                      onClick={() =>
                        setEditingField(
                          isEditing ? null : { zone, rowIdx, fieldIdx }
                        )
                    }
                    >
                      <div className="flex items-center gap-1">
                        <FaGripVertical className="text-gray-300 cursor-grab" />
                        <span className="font-medium">{fieldLabel(field.fieldId)}</span>
                        {field.width && field.width !== "full" && (
                          <span className="text-gray-400">({field.width})</span>
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeField(zone, rowIdx, fieldIdx);
                          }}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <FaTrash className="text-[10px]" />
                        </button>
                      </div>

                      {isEditing && (
                        <div className="absolute top-full left-0 z-10 mt-1 bg-white border rounded shadow-lg p-2 min-w-[200px]" onClick={(e) => e.stopPropagation()}>
                          <div className="space-y-2">
                            <div>
                              <label className="text-[10px] text-gray-500 block">Width</label>
                              <select
                                className="w-full text-xs border rounded px-1 py-0.5"
                                value={field.width || "full"}
                                onChange={(e) => updateFieldConfig(zone, rowIdx, fieldIdx, { width: e.target.value })}
                              >
                                {WIDTH_OPTIONS.map((w) => (
                                  <option key={w} value={w}>{w}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-[10px] text-gray-500 block">Font size</label>
                              <select
                                className="w-full text-xs border rounded px-1 py-0.5"
                                value={field.fontSize || "sm"}
                                onChange={(e) => updateFieldConfig(zone, rowIdx, fieldIdx, { fontSize: e.target.value })}
                              >
                                {FONT_SIZE_OPTIONS.map((s) => (
                                  <option key={s} value={s}>{s}</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex gap-2">
                              <label className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={field.bold || false}
                                  onChange={(e) => updateFieldConfig(zone, rowIdx, fieldIdx, { bold: e.target.checked })}
                                />
                                Bold
                              </label>
                              <label className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={field.italic || false}
                                  onChange={(e) => updateFieldConfig(zone, rowIdx, fieldIdx, { italic: e.target.checked })}
                                />
                                Italic
                              </label>
                            </div>
                            <div className="flex gap-2">
                              <label className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={field.showLabel ?? true}
                                  onChange={(e) => updateFieldConfig(zone, rowIdx, fieldIdx, { showLabel: e.target.checked })}
                                />
                                Show label
                              </label>
                              <label className="flex items-center gap-1 text-xs">
                                <input
                                  type="checkbox"
                                  checked={field.showIcon ?? true}
                                  onChange={(e) => updateFieldConfig(zone, rowIdx, fieldIdx, { showIcon: e.target.checked })}
                                />
                                Show icon
                              </label>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}

                {row.fields.length === 0 && (
                  <span className="text-xs text-gray-400 italic">Empty row</span>
                )}

                <select
                  className="text-xs border rounded px-1 py-0.5 bg-white"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) addFieldToRow(zone, rowIdx, e.target.value);
                  }}
                >
                  <option value="">+ Add field</option>
                  {allFields.map((f) => (
                    <option key={f.id} value={f.id}>{f.label}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold mb-1">Card Layout</h3>
        <p className="text-sm text-gray-500 mb-4">
          Design how cards are displayed. Organize fields into rows within each zone (Header, Body, Footer).
          Multiple fields per row appear side by side. Click a field to edit its display options.
        </p>
      </div>

      <div className="space-y-3">
        {["header", "body", "footer"].map((zone) => renderZone(zone))}
      </div>

      <div className="flex justify-between items-center pt-2">
        <button
          onClick={() => onChange(defaultLayout())}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          Reset to default
        </button>
        <Button onClick={onSave}>
          <FaSave className="inline mr-2" /> Save card layout
        </Button>
      </div>
    </div>
  );
}
